/**
 * Party frames — the small roster column under #hud-location (spec:
 * docs/superpowers/specs/2026-07-18-social-systems-design.md §3/§8).
 *
 * Read-only HUD furniture in the title screen's language (see ui/meridian.ts):
 * each member is a hairline SPINE with their name tracked above a 2px vitals
 * bar in the party strand — no cards, no borders. OFFLINE (inside the
 * departure grace) reads in the sign-in screen's red. Every party ACTION lives
 * in the social panel; this widget never takes the pointer.
 *
 * Names here come from the server's character-name alphabet, so template
 * interpolation is safe (chat text never reaches this file).
 */
import type { PartyModel } from "../rpg/party";

export class PartyFrames {
  private root: HTMLElement;
  private lastRev = -1;
  private lastSystem = "";

  constructor() {
    this.root = document.createElement("div");
    this.root.id = "party-frames";
    this.root.style.cssText = [
      "position:fixed", "left:16px", "width:216px", "z-index:7",
      "display:none", "flex-direction:column",
      "pointer-events:none", "text-shadow:0 1px 3px rgba(0,0,0,0.9)",
    ].join(";");
    document.body.appendChild(this.root);
  }

  /** Cheap on every frame: re-renders only when the model or my system moved.
   *  `mySystem` decides which members read as "elsewhere". */
  update(party: PartyModel | null, mySystem: string): void {
    if (!party || !party.inParty) {
      this.root.style.display = "none";
      this.lastRev = -1;
      return;
    }
    if (party.rev === this.lastRev && mySystem === this.lastSystem) return;
    this.lastRev = party.rev;
    this.lastSystem = mySystem;
    // Sit just under the location panel wherever it happens to end.
    const loc = document.getElementById("hud-location")?.getBoundingClientRect();
    this.root.style.top = `${loc ? Math.round(loc.bottom) + 10 : 120}px`;
    this.root.style.display = "flex";
    this.root.innerHTML = party.members.map((m) => {
      const frac = m.online && m.maxHp ? Math.max(0, Math.min(1, (m.hp ?? 0) / m.maxHp)) : 0;
      const bar = m.online
        ? `<div class="pf-track"><span class="pf-bar${frac <= 0.3 ? " hurt" : ""}" style="width:${
            (frac * 100).toFixed(0)}%"></span></div>`
        : "";
      const where = m.online
        ? `<span class="pf-where">${m.system && m.system !== mySystem ? m.system : "here"}</span>`
        : `<span class="pf-where off">offline</span>`;
      return `<div class="pf-row${m.me ? " me" : ""}${m.online ? "" : " off"}">
        <div class="pf-head">
          <span class="pf-name">${m.leader ? "✦ " : ""}${m.name}</span>${where}
        </div>${bar}
      </div>`;
    }).join("");
  }
}
